import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Printer, RefreshCw, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOT_CLASSES = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-rose-500",
};

const RING_CLASSES = {
  green: "ring-emerald-500/30 bg-emerald-50 border-emerald-200",
  yellow: "ring-amber-500/30 bg-amber-50 border-amber-200",
  red: "ring-rose-500/30 bg-rose-50 border-rose-300",
};

const ICON_FOR = {
  green: <CheckCircle2 className="h-5 w-5 text-emerald-600" />,
  yellow: <Clock className="h-5 w-5 text-amber-600" />,
  red: <AlertTriangle className="h-5 w-5 text-rose-600" />,
};

/** Small "is the kitchen printer alive?" widget on the admin dashboard.
 *  Polls /api/admin/print/health every 15s. Shows overall status,
 *  seconds since last poll, and stuck-queue count. */
export default function PrinterHealthWidget({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/admin/print/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load printer health");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading && !data) {
    return (
      <div
        className="rounded-2xl border border-[#E7E5E4] bg-white px-5 py-4 flex items-center gap-3"
        data-testid="printer-health-loading"
      >
        <Printer className="h-5 w-5 text-[#57534E]" />
        <span className="text-sm text-[#57534E]">Checking printer…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-2xl border border-rose-300 bg-rose-50 px-5 py-4"
        data-testid="printer-health-error"
      >
        <div className="text-sm text-rose-700">Printer health: {error}</div>
      </div>
    );
  }

  const overall = data?.overall || "red";
  const label = data?.overall_label || "Unknown";
  const bestBeat = data?.heartbeats?.[0];
  const seconds = bestBeat?.seconds_since_poll;
  const pendingStuck = data?.pending_over_5min || 0;
  const printedToday = data?.printed_last_24h || 0;

  return (
    <div
      className={`rounded-2xl border px-5 py-4 ring-1 ${RING_CLASSES[overall]}`}
      data-testid="printer-health"
      data-status={overall}
    >
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <div className={`h-3 w-3 rounded-full ${DOT_CLASSES[overall]} animate-pulse`} />
          {ICON_FOR[overall]}
          <div>
            <div className="text-xs tracking-widest uppercase font-bold text-[#0E0E1A]">
              Kitchen Printer
            </div>
            <div className="text-sm text-[#0E0E1A] font-medium" data-testid="printer-health-label">
              {label}
            </div>
          </div>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-5 text-xs text-[#57534E]">
          {seconds != null && (
            <div>
              <span className="opacity-70">Last poll:&nbsp;</span>
              <span className="font-bold text-[#0E0E1A]" data-testid="printer-last-poll">
                {seconds < 5 ? "just now" : `${seconds}s ago`}
              </span>
            </div>
          )}
          <div>
            <span className="opacity-70">Stuck in queue:&nbsp;</span>
            <span
              className={`font-bold ${pendingStuck > 0 ? "text-rose-600" : "text-[#0E0E1A]"}`}
              data-testid="printer-stuck-count"
            >
              {pendingStuck}
            </span>
          </div>
          <div>
            <span className="opacity-70">Printed today:&nbsp;</span>
            <span className="font-bold text-[#0E0E1A]" data-testid="printer-printed-today">
              {printedToday}
            </span>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1 rounded-full border border-current px-3 py-1 hover:bg-white/60"
            data-testid="printer-health-refresh"
            title="Refresh"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      {overall === "red" && (
        <div className="mt-3 text-xs text-rose-700 border-t border-rose-200 pt-2" data-testid="printer-health-hint">
          <span className="font-bold">Troubleshoot:</span> ① Check the Star printer is powered on and connected to the internet. ② Open its web dashboard and confirm the CloudPRNT Server URL matches this site. ③ Once it polls again, this dot goes green within 30 seconds.
        </div>
      )}
    </div>
  );
}
